<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="text-center mb-8">
          <h1 class="text-h3 mb-4">Suivi de votre candidature</h1>
          <p class="text-h6 text-grey">
            Bonjour {{ candidateName }}, voici le statut de votre candidature
          </p>
        </div>
      </v-col>
    </v-row>

    <!-- Application Status Card -->
    <v-row class="justify-center">
      <v-col cols="12" md="8" lg="6">
        <v-card class="elevation-8">
          <v-card-title class="text-center py-6">
            <v-icon :color="getStatusColor(applicationStatus)" size="48" class="mb-4">
              {{ getStatusIcon(applicationStatus) }}
            </v-icon>
            <div class="text-h5">{{ getStatusTitle(applicationStatus) }}</div>
          </v-card-title>

          <v-card-text>
            <div class="text-center mb-6">
              <v-chip :color="getStatusColor(applicationStatus)" size="large" class="px-6 py-2">
                {{ getStatusText(applicationStatus) }}
              </v-chip>
            </div>

            <p class="text-body-1 text-center mb-4">
              {{ getStatusDescription(applicationStatus) }}
            </p>

            <!-- Progress Steps -->
            <v-stepper v-model="currentStep" :items="steps" class="elevation-0" hide-actions>
              <template v-slot:item.icon="{ item, step }">
                <v-icon :color="step <= currentStep ? 'primary' : 'grey'" size="24">
                  {{ item.icon }}
                </v-icon>
              </template>
            </v-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Application Details -->
    <v-row class="justify-center mt-6">
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title>Détails de votre candidature</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <strong>Poste:</strong>
              </v-col>
              <v-col cols="6">
                {{ applicationDetails.jobTitle }}
              </v-col>

              <v-col cols="6">
                <strong>Date de candidature:</strong>
              </v-col>
              <v-col cols="6">
                {{ formatDate(applicationDetails.appliedAt) }}
              </v-col>

              <v-col cols="6">
                <strong>Référence:</strong>
              </v-col>
              <v-col cols="6">
                {{ applicationDetails.reference }}
              </v-col>

              <v-col cols="6">
                <strong>Dernière mise à jour:</strong>
              </v-col>
              <v-col cols="6">
                {{ formatDate(applicationDetails.lastUpdate) }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Card Questionnaire & Vidéo (SUBMITTED, UNDER_REVIEW) -->
    <v-row class="justify-center mt-6" v-if="['SUBMITTED', 'UNDER_REVIEW'].includes(applicationStatus)">
      <v-col cols="12" md="8" lg="6">
        <v-card color="primary" variant="tonal">
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2">mdi-comment-question-outline</v-icon>
            Répondez au questionnaire et enregistrez votre présentation vidéo
          </v-card-title>
          <v-card-text>
            <v-btn color="primary" v-if="!questionnaireRepondu" prepend-icon="mdi-file-document-edit" class="mb-4"
              @click="openQuestionnaire">
              Répondre au questionnaire
            </v-btn>
            <v-btn color="secondary" prepend-icon="mdi-video" class="mb-4" @click="openVideoDialog">
              Enregistrer une vidéo de présentation
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Message de rejet (REJECTED) -->
    <v-row class="justify-center mt-6" v-if="applicationStatus === 'REJECTED'">
      <v-col cols="12" md="8" lg="6">
        <v-alert type="error" border="start" prominent>
          <v-alert-title>Candidature non retenue</v-alert-title>
          Nous vous remercions pour l'intérêt porté à notre entreprise. N'hésitez pas à postuler à d'autres offres !
        </v-alert>
      </v-col>
    </v-row>

    <!-- Choix de l'entretien (SHORTLISTED) -->
    <v-row class="justify-center mt-6" v-if="applicationStatus === 'SHORTLISTED'">
      <v-col cols="12" md="8" lg="6">
        <v-card color="info" variant="tonal">
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2">mdi-calendar-plus</v-icon>
            Choisissez votre créneau d'entretien
          </v-card-title>
          <v-card-text>
            <v-btn color="primary" prepend-icon="mdi-calendar-clock">
              Choisir un créneau
            </v-btn>
            <div class="text-caption text-grey mt-2">Vous recevrez une confirmation par email après validation.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Interview Details (SELECTED, ACCEPTED) -->
    <v-row class="justify-center mt-6" v-if="['SELECTED', 'ACCEPTED'].includes(applicationStatus) && interviewDetails">
      <v-col cols="12" md="8" lg="6">
        <v-card color="info" variant="tonal">
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2">mdi-calendar-clock</v-icon>
            Entretien programmé
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <strong>Date:</strong>
              </v-col>
              <v-col cols="6">
                {{ formatDateTime(interviewDetails.date) }}
              </v-col>

              <v-col cols="6">
                <strong>Durée:</strong>
              </v-col>
              <v-col cols="6">
                {{ interviewDetails.duration }}
              </v-col>

              <v-col cols="6">
                <strong>Type:</strong>
              </v-col>
              <v-col cols="6">
                {{ interviewDetails.type }}
              </v-col>

              <v-col cols="6" v-if="interviewDetails.location">
                <strong>Lieu:</strong>
              </v-col>
              <v-col cols="6" v-if="interviewDetails.location">
                {{ interviewDetails.location }}
              </v-col>

              <v-col cols="6" v-if="interviewDetails.meetingLink">
                <strong>Lien:</strong>
              </v-col>
              <v-col cols="6" v-if="interviewDetails.meetingLink">
                <a :href="interviewDetails.meetingLink" target="_blank">
                  Rejoindre la réunion
                </a>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <div class="d-flex justify-center">
              <v-btn color="primary" prepend-icon="mdi-calendar-plus" @click="addToCalendar">
                Ajouter au calendrier
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Next Steps -->
    <v-row class="justify-center mt-6" v-if="nextSteps.length > 0">
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title>Prochaines étapes</v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item v-for="(step, index) in nextSteps" :key="index" class="px-0">
                <template v-slot:prepend>
                  <v-icon :color="step.color">{{ step.icon }}</v-icon>
                </template>
                <v-list-item-title>{{ step.title }}</v-list-item-title>
                <v-list-item-subtitle>{{ step.description }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Contact Information -->
    <v-row class="justify-center mt-6" hidden>
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title>Besoin d'aide?</v-card-title>
          <v-card-text>
            <p class="mb-4">
              Si vous avez des questions concernant votre candidature,
              n'hésitez pas à nous contacter.
            </p>

            <div class="d-flex flex-column gap-2">
              <div class="d-flex align-center">
                <v-icon class="me-2">mdi-email</v-icon>
                <span>recrutement@entreprise.com</span>
              </div>
              <div class="d-flex align-center">
                <v-icon class="me-2">mdi-phone</v-icon>
                <span>+212 5 51 32 99</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modal questionnaire -->
    <v-dialog v-model="showQuestionnaireDialog" max-width="600">
      <v-card>
        <v-card-title>
          Questionnaire
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="questionnaire.length">
          <div>
            <div class="mb-4">
              <strong>Question {{ currentQuestionIndex + 1 }} / {{ questionnaire.length }}</strong>
            </div>
            <div class="mb-2">
              {{ questionnaire[currentQuestionIndex].question }}
            </div>
            <v-radio-group v-model="questionnaire[currentQuestionIndex].candidat_answer"
              :disabled="submittingQuestionnaire">
              <v-radio v-for="choice in questionnaire[currentQuestionIndex].choices" :key="choice" :label="choice"
                :value="choice" />
            </v-radio-group>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="prevQuestion" :disabled="currentQuestionIndex === 0">
            Précédent
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="nextQuestion" :disabled="currentQuestionIndex === questionnaire.length - 1">
            Suivant
          </v-btn>
          <v-btn color="primary" :loading="submittingQuestionnaire" @click="submitQuestionnaire"
            v-if="currentQuestionIndex === questionnaire.length - 1">
            Soumettre
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal vidéo -->
    <v-dialog v-model="showVideoDialog" max-width="600">
      <v-card>
        <v-card-title>Enregistrez votre vidéo de présentation</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-if="!videoUrl">
            <video id="video-preview" style="width:100%;background:#000;" autoplay playsinline></video>
            <div class="d-flex justify-center mt-4">
              <v-btn color="primary" @click="startRecording" v-if="!recording">Démarrer l'enregistrement</v-btn>
              <v-btn color="error" @click="stopRecording" v-if="recording">Arrêter</v-btn>
            </div>
          </div>
          <div v-else>
            <video :src="videoUrl" controls style="width:100%;margin-top:16px;" />
            <div class="d-flex justify-center mt-4">
              <v-btn color="primary" :loading="uploadingVideo" @click="submitVideo">Envoyer la vidéo</v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { apiService } from '@/services/api'
import { useCandidateStore } from '@/stores/candidate'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { computed, ref } from 'vue'

const candidateStore = useCandidateStore()

const currentApplication = computed(() => candidateStore.currentApplication)
const applications = computed(() => candidateStore.applications)

const candidateName = computed(() => currentApplication.value?.fullName || `${currentApplication.value?.prenom || ''} ${currentApplication.value?.nom || ''}`.trim() || 'Candidat')
const applicationStatus = computed(() => currentApplication.value?.status || 'SUBMITTED')
const applicationDetails = computed(() => ({
  jobTitle: currentApplication.value?.fichePosteTitle || '',
  appliedAt: currentApplication.value?.appliedAt || '',
  reference: currentApplication.value?.id || '',
  lastUpdate: currentApplication.value?.lastUpdate || currentApplication.value?.appliedAt || ''
}))
const interviewDetails = computed(() => currentApplication.value?.interviewDetails || null)
const candidatEval = computed(() => currentApplication.value?.candidatEval)
const questionnaireRepondu = computed(() => currentApplication.value?.questionnaireRepondu)
const presentation = computed(() => currentApplication.value?.presentation)

const currentStep = ref(3)

const steps = [
  {
    title: 'Candidature reçue',
    icon: 'mdi-file-document-check',
    subtitle: 'Votre candidature a été reçue'
  },
  {
    title: 'Examen du dossier',
    icon: 'mdi-account-search',
    subtitle: 'Analyse de votre profil'
  },
  {
    title: 'Entretien',
    icon: 'mdi-calendar-account',
    subtitle: 'Entretien avec l\'équipe'
  },
  {
    title: 'Décision finale',
    icon: 'mdi-check-circle',
    subtitle: 'Communication de la décision'
  }
]

const nextSteps = computed(() => {
  switch (applicationStatus.value) {
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      return [
        {
          title: 'Complétez votre dossier',
          description: 'Répondez au questionnaire et ajoutez une vidéo de présentation pour valoriser votre profil.',
          icon: 'mdi-comment-question-outline',
          color: 'primary'
        }
      ]
    case 'SHORTLISTED':
      return [
        {
          title: 'Choisissez un créneau',
          description: 'Sélectionnez une date pour votre entretien.',
          icon: 'mdi-calendar-plus',
          color: 'info'
        }
      ]
    case 'SELECTED':
      return [
        {
          title: 'Préparez votre entretien',
          description: 'Consultez les détails de votre entretien ci-dessous.',
          icon: 'mdi-calendar-clock',
          color: 'primary'
        }
      ]
    case 'ACCEPTED':
      return [
        {
          title: 'Félicitations!',
          description: 'Vous avez été sélectionné(e) pour ce poste',
          icon: 'mdi-party-popper',
          color: 'success'
        }
      ]
    case 'REJECTED':
      return []
    default:
      return []
  }
})

const getStatusColor = (status: string) => {
  const colors = {
    SUBMITTED: 'grey',
    UNDER_REVIEW: 'warning',
    SHORTLISTED: 'info',
    SELECTED: 'primary',
    ACCEPTED: 'success',
    REJECTED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status: string) => {
  const icons = {
    SUBMITTED: 'mdi-file-document-check',
    UNDER_REVIEW: 'mdi-clock-outline',
    SHORTLISTED: 'mdi-account-search',
    SELECTED: 'mdi-calendar-account',
    ACCEPTED: 'mdi-check-circle',
    REJECTED: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help-circle'
}

const getStatusTitle = (status: string) => {
  const titles = {
    SUBMITTED: 'Candidature soumise',
    UNDER_REVIEW: 'Candidature en cours d\'examen',
    SHORTLISTED: 'Dossier examiné',
    SELECTED: 'Entretien programmé',
    ACCEPTED: 'Candidature acceptée',
    REJECTED: 'Candidature non retenue'
  }
  return titles[status] || 'Statut inconnu'
}

const getStatusText = (status: string) => {
  const texts = {
    SUBMITTED: 'soumis',
    UNDER_REVIEW: 'En examen',
    SHORTLISTED: 'liste restreinte',
    SELECTED: 'sélectionné',
    ACCEPTED: 'accepté',
    REJECTED: 'rejeté'
  }
  return texts[status] || status
}

const getStatusDescription = (status: string) => {
  const descriptions = {
    SUBMITTED: 'Votre candidature a été reçue et n\'est pas encore examinée.',
    UNDER_REVIEW: 'Votre candidature est en cours d\'examen par notre équipe.',
    SHORTLISTED: 'Votre dossier a été examiné. Nous vous recontacterons prochainement.',
    SELECTED: 'Votre profil nous intéresse! Un entretien a été programmé.',
    ACCEPTED: 'Félicitations! Votre candidature a été retenue pour ce poste.',
    REJECTED: 'Après examen, nous ne pouvons pas donner suite à votre candidature pour ce poste.'
  }
  return descriptions[status] || 'Statut de candidature'
}

const formatDate = (date: string) => {
  return date ? format(new Date(date), 'dd/MM/yyyy', { locale: fr }) : ''
}

const formatDateTime = (date: string) => {
  return date ? format(new Date(date), 'dd/MM/yyyy à HH:mm', { locale: fr }) : ''
}

const addToCalendar = () => {
  if (!interviewDetails.value) return
  const startDate = new Date(interviewDetails.value.date)
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000)
  const event = {
    title: `Entretien - ${applicationDetails.value.jobTitle}`,
    start: startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z',
    end: endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z',
    description: `Entretien pour le poste de ${applicationDetails.value.jobTitle}`,
    location: interviewDetails.value.location || 'Visioconférence'
  }
  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start}/${event.end}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`
  window.open(googleCalendarUrl, '_blank')
}

// Questionnaire logic
const showQuestionnaireDialog = ref(false)
const questionnaire = ref<any[]>([])
const currentQuestionIndex = ref(0)
const loadingQuestionnaire = ref(false)
const submittingQuestionnaire = ref(false)

// Ajout d'une variable pour stocker l'ID du questionnaire chargé
const loadedQuestionnaireId = ref<string | undefined>(undefined)

// Ouvre la modale et charge le questionnaire
const openQuestionnaire = async () => {
  loadingQuestionnaire.value = true
  try {
    const fichePosteId = currentApplication.value?.fichePosteId
    if (!fichePosteId) throw new Error('Aucune fiche de poste sélectionnée')
    const res = await apiService.candidateSpace.getQuestionnaires(fichePosteId)
    loadedQuestionnaireId.value = res.data?.id // Stocke l'ID du questionnaire pour la soumission
    questionnaire.value = (JSON.parse(res.data?.questionsJson) || []).map((q: any) => ({
      ...q,
      candidat_answer: ''
    }))
    currentQuestionIndex.value = 0
    showQuestionnaireDialog.value = true
  } catch (e) {
    console.error('Erreur lors du chargement du questionnaire:', e)
  } finally {
    loadingQuestionnaire.value = false
  }
}

// Navigation
const nextQuestion = () => {
  if (currentQuestionIndex.value < questionnaire.value.length - 1) currentQuestionIndex.value++
}
const prevQuestion = () => {
  if (currentQuestionIndex.value > 0) currentQuestionIndex.value--
}

// Soumission
const submitQuestionnaire = async () => {
  submittingQuestionnaire.value = true
  try {
    const questionnaireId = loadedQuestionnaireId.value
    if (!questionnaireId) throw new Error('Aucun questionnaire chargé')
    currentApplication.value.questionnaireRepondu = JSON.stringify(questionnaire.value)
    await apiService.candidateSpace.submitQuestionnaire(questionnaireId, currentApplication.value)
    showQuestionnaireDialog.value = false
    // Afficher un message de succès
  } catch (e) {
    console.error('Erreur lors de la soumission du questionnaire:', e)
  } finally {
    submittingQuestionnaire.value = false
  }
}

// Vidéo logic
const showVideoDialog = ref(false)
const recording = ref(false)
const videoUrl = ref<string | null>(null)
const videoFile = ref<File | null>(null)
const uploadingVideo = ref(false)
const mediaStream = ref<MediaStream | null>(null)
let mediaRecorder: MediaRecorder | null = null
let recordedChunks: Blob[] = []

const openVideoDialog = async () => {
  showVideoDialog.value = true
  videoUrl.value = null
  videoFile.value = null
  recordedChunks = []
}

const startRecording = async () => {
  try {
    mediaStream.value = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
    const videoElement = document.getElementById('video-preview') as HTMLVideoElement
    if (videoElement && mediaStream.value) {
      videoElement.srcObject = mediaStream.value
      videoElement.muted = true
      videoElement.play()
    }
    mediaRecorder = new MediaRecorder(mediaStream.value)
    recordedChunks = []
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunks.push(e.data)
    }
    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunks, { type: 'video/webm' })
      videoUrl.value = URL.createObjectURL(blob)
      videoFile.value = new File([blob], 'presentation.webm', { type: 'video/webm' })
      if (mediaStream.value) {
        mediaStream.value.getTracks().forEach(track => track.stop())
        mediaStream.value = null
      }
    }
    mediaRecorder.start()
    recording.value = true
  } catch (e) {
    alert('Impossible d’accéder à la caméra.')
  }
}

const stopRecording = () => {
  if (mediaRecorder && recording.value) {
    mediaRecorder.stop()
    recording.value = false
  }
}

const submitVideo = async () => {
  if (!videoFile.value) return
  uploadingVideo.value = true
  try {
    const candidatureId = currentApplication.value?.id
    if (!candidatureId) throw new Error('Aucune candidature sélectionnée')
    await apiService.candidate.uploadVideo(candidatureId, videoFile.value)
    showVideoDialog.value = false
    // Afficher un message de succès
  } catch (e) {
    // Gérer l'erreur
  } finally {
    uploadingVideo.value = false
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.v-stepper {
  box-shadow: none !important;
}
</style>
